import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Copy, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_LABEL = { manikure: "Apparative Maniküre", pedikure: "Apparative Pediküre" };
const REST_AMOUNT = { manikure: 629.3, pedikure: 839.3 };
const STATUS_LABEL = {
  confirmed: "Bezahlt",
  pending_payment: "Zahlung offen",
  expired: "Abgelaufen",
  cancelled: "Storniert",
  failed: "Fehlgeschlagen",
  forfeited: "Verfallen",
};

const GRID = "grid grid-cols-[1.4fr_1fr_0.7fr_0.9fr_0.8fr_1.2fr] gap-3 px-4";

const ValidityNote = ({ date }) => {
  const start = new Date(`${date}T00:00:00`);
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  const now = new Date();
  const fmt = (d) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  if (now < start) return <div className="mt-1 text-[11px] text-amber-600">frei ab {fmt(start)} · gültig bis {fmt(end)}</div>;
  if (now > end) return <div className="mt-1 text-[11px] text-red-500">Zugang abgelaufen am {fmt(end)}</div>;
  return <div className="mt-1 text-[11px] text-primary">Zugang gültig bis {fmt(end)}</div>;
};

export const AcademyTab = ({ pin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/academy/bookings`, { pin });
      setItems(data);
    } catch {
      toast.error("Academy-Buchungen konnten nicht geladen werden");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code ${code} kopiert`);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const refundDeposit = async (b) => {
    const ok = window.confirm(
      `Wirklich ${b.amount.toFixed(2).replace(".", ",")} € (Anzahlung) an ${b.first_name} ${b.last_name} erstatten?\n\nDer Betrag wird sofort über Stripe zurückgezahlt und die Schülerin per E-Mail informiert.`
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/academy/refund-deposit`, { pin, booking_id: b.id });
      toast.success(`Anzahlung an ${b.first_name} ${b.last_name} erstattet`);
      load();
    } catch {
      toast.error("Erstattung fehlgeschlagen – bitte im Stripe-Dashboard prüfen");
    }
  };

  const cancelBooking = async (b) => {
    const total = b.amount + (b.rest_paid ? REST_AMOUNT[b.course] || 0 : 0);
    const ok = window.confirm(
      `Buchung von ${b.first_name} ${b.last_name} wirklich stornieren?\n\n${total.toFixed(2).replace(".", ",")} € werden sofort über Stripe erstattet, der Termin wird wieder frei und der Zugangscode ungültig. Die Schülerin wird per E-Mail informiert.`
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(`${API}/admin/academy/cancel`, { pin, booking_id: b.id, refund: true });
      toast.success(`Buchung storniert${data.refunded ? ` · ${data.refunded.toFixed(2).replace(".", ",")} € erstattet` : ""}`);
      load();
    } catch {
      toast.error("Stornierung fehlgeschlagen – bitte im Stripe-Dashboard prüfen");
    }
  };

  const deleteBooking = async (b) => {
    const ok = window.confirm(
      `Eintrag von ${b.first_name} ${b.last_name} (${fmtDate(b.date)}) endgültig löschen?\n\nDer Termin wird sofort wieder frei. Es wird KEINE Erstattung ausgelöst – bitte nur für Test-/Fehlbuchungen nutzen.`
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/academy/delete`, { pin, booking_id: b.id });
      toast.success("Eintrag gelöscht");
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const restLinkDate = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() - 7);
    return fmtDate(d.toISOString().slice(0, 10));
  };

  const fmtDate = (iso) => {
    try {
      return new Date(`${iso}T12:00:00`).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const fmtAmount = (b) =>
    `${b.amount.toFixed(2).replace(".", ",")} € ${b.payment_option === "deposit" ? "(Anzahlung)" : "(Gesamt)"}`;

  return (
    <div data-testid="tab-academy" className="max-w-6xl">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-2xl">Academy · Schülerinnen & Zugangscodes</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Jede Schülerin erhält nach Zahlung automatisch ihren persönlichen Code per E-Mail. Der Code wird am Schulungstag um 0:00 Uhr freigeschaltet.
          </p>
        </div>
        <button data-testid="academy-refresh" onClick={load} className="group flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground">
          <RefreshCw size={14} className="group-hover:rotate-180 transition-transform duration-500" /> Aktualisieren
        </button>
      </div>

      <div className="border border-border overflow-x-auto">
        <div className={`${GRID} py-2.5 border-b border-border bg-muted text-[10px] uppercase tracking-widest text-muted-foreground min-w-[900px]`}>
          <div>Name & Kontakt</div>
          <div>Kurs</div>
          <div>Termin</div>
          <div>Betrag</div>
          <div>Status</div>
          <div>Zugangscode</div>
        </div>
        {items.map((b) => (
          <div key={b.id} data-testid={`academy-row-${b.id}`} className={`${GRID} py-3 border-b border-border items-center text-sm min-w-[900px]`}>
            <div>
              <div className="font-medium">{b.first_name} {b.last_name}</div>
              <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Mail size={11} /> {b.email}</a>
              <a href={`tel:${b.phone}`} className="ml-2 inline-flex items-center gap-1 text-xs hover:text-primary"><Phone size={11} /> {b.phone}</a>
            </div>
            <div className="text-xs">{COURSE_LABEL[b.course] || b.course}</div>
            <div className="text-xs">{fmtDate(b.date)}</div>
            <div className="text-xs tabular-nums">{fmtAmount(b)}</div>
            <div>
              <span className={`inline-block px-2 py-1 text-[11px] border ${b.status === "confirmed" ? "border-primary/40 text-primary" : "border-border text-foreground/50"}`}>
                {STATUS_LABEL[b.status] || b.status}
              </span>
              {b.status === "cancelled" && b.cancel_refunded_amount > 0 && (
                <div data-testid={`academy-cancel-refunded-${b.id}`} className="mt-1 text-[11px] text-foreground/50">
                  {b.cancel_refunded_amount.toFixed(2).replace(".", ",")} € erstattet
                </div>
              )}
              {b.status === "confirmed" && (
                <button
                  type="button"
                  data-testid={`academy-cancel-${b.id}`}
                  onClick={() => cancelBooking(b)}
                  className="mt-1.5 inline-block px-2 py-1 text-[11px] border border-red-300 text-red-600 transition-colors hover:bg-red-50"
                >
                  Stornieren & erstatten
                </button>
              )}
              {b.status !== "confirmed" && (
                <button
                  type="button"
                  data-testid={`academy-delete-${b.id}`}
                  onClick={() => deleteBooking(b)}
                  className="mt-1.5 inline-block text-[11px] text-foreground/35 underline decoration-dotted transition-colors hover:text-red-500"
                >
                  Eintrag löschen
                </button>
              )}
              {b.payment_option === "deposit" && (b.status === "confirmed" || b.status === "forfeited") && (
                <div data-testid={`academy-rest-${b.id}`} className="mt-1 text-[11px]">
                  {b.status === "forfeited" ? (
                    <>
                      <span className="text-red-500">Restbetrag nicht gezahlt – Termin verfallen</span>
                      {b.deposit_refunded ? (
                        <span data-testid={`academy-refunded-${b.id}`} className="mt-1.5 inline-block px-2 py-1 text-[11px] border border-primary/50 text-primary bg-primary/5">
                          Anzahlung erstattet ✓{b.deposit_refunded_at ? ` · ${new Date(b.deposit_refunded_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`academy-refund-${b.id}`}
                          onClick={() => refundDeposit(b)}
                          className="mt-1.5 inline-block px-2 py-1 text-[11px] border border-border text-foreground/60 transition-colors hover:border-primary/40 hover:text-primary"
                        >
                          Anzahlung über Stripe erstatten
                        </button>
                      )}
                    </>
                  ) : b.rest_paid ? (
                    <span className="text-primary">Restbetrag {(REST_AMOUNT[b.course] || 0).toFixed(2).replace(".", ",")} € bezahlt ✓</span>
                  ) : b.rest_link_sent ? (
                    <span className="text-amber-600">Restbetrag angefordert · {(REST_AMOUNT[b.course] || 0).toFixed(2).replace(".", ",")} € offen{b.rest_due ? ` · Frist ${fmtDate(b.rest_due)}` : ""}</span>
                  ) : (
                    <span className="text-foreground/40">Restlink am {restLinkDate(b.date)} · Rest {(REST_AMOUNT[b.course] || 0).toFixed(2).replace(".", ",")} €</span>
                  )}
                </div>
              )}
            </div>
            <div>
              {b.access_code ? (
                <>
                  <div className="flex items-center gap-2">
                    <code data-testid={`academy-code-${b.id}`} className="text-sm tracking-widest font-mono">{b.access_code}</code>
                    <button
                      type="button"
                      data-testid={`academy-copy-${b.id}`}
                      onClick={() => copyCode(b.access_code)}
                      className="text-foreground/40 hover:text-primary transition-colors"
                      title="Code kopieren"
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                  <div data-testid={`academy-validity-${b.id}`}><ValidityNote date={b.date} /></div>
                </>
              ) : (
                <span className="text-xs text-muted-foreground italic">–</span>
              )}
            </div>
          </div>
        ))}
        {!items.length && !loading && (
          <p className="p-6 text-center text-sm text-muted-foreground">Noch keine Academy-Buchungen vorhanden.</p>
        )}
      </div>
    </div>
  );
};
